<template>
  <div class="legal-page">
    <!-- Navbar -->
    <MainNavbar />

    <!-- Hero Banner -->
    <div class="legal-hero">
      <div class="hero-content">
        <h1>Terms of Use</h1>
        <p>Last Updated: April 27, 2026</p>
      </div>
      <LegalDocumentSeal />
    </div>

    <!-- Main Content -->
    <div class="legal-container">
      <div class="legal-content">
        <!-- Introduction -->
        <div class="legal-section">
          <h2>IpromiseAfrica Website Terms of Use</h2>
          <p class="legal-intro">
            These Terms and Conditions of Use (the "Terms of Use") apply to the IpromiseAfrica web site 
            located at www.ipromiseafrica.ke, and all associated sites linked to www.ipromiseafrica.ke 
            by IpromiseAfrica, its subsidiaries and affiliates, including IpromiseAfrica sites around 
            the world (collectively, the "Site"). The Site is the property of IpromiseAfrica Inc. 
            ("IpromiseAfrica") and its licensors. BY USING THE SITE, YOU AGREE TO THESE TERMS OF USE; 
            IF YOU DO NOT AGREE, DO NOT USE THE SITE.
          </p>
          <p class="legal-notice">
            IpromiseAfrica reserves the right, at its sole discretion, to change, modify, add or remove 
            portions of these Terms of Use, at any time. It is your responsibility to check these Terms 
            of Use periodically for changes. Your continued use of the Site following the posting of 
            changes will mean that you accept and agree to the changes.
          </p>
        </div>

        <!-- Ownership of Site -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('ownership')">
              <span>Ownership of Site; Agreement to Terms of Use</span>
              <i :class="dropdownStates.ownership ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.ownership">
              <p>
                As long as you comply with these Terms of Use, IpromiseAfrica grants you a personal, 
                non-exclusive, non-transferable, limited privilege to enter and use the Site.
              </p>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('content')">
              <span>Content</span>
              <i :class="dropdownStates.content ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.content">
              <p>
                All text, graphics, user interfaces, visual interfaces, photographs, trademarks, logos, 
                sounds, music, artwork and computer code (collectively, "Content"), including but not 
                limited to the design, structure, selection, coordination, expression, "look and feel" 
                and arrangement of such Content, contained on the Site is owned, controlled or licensed 
                by or to IpromiseAfrica.
              </p>
              <p>
                Except as expressly provided in these Terms of Use, no part of the Site and no Content 
                may be copied, reproduced, republished, uploaded, posted, publicly displayed, encoded, 
                translated, transmitted or distributed in any way (including "mirroring") to any other 
                computer, server, Web site or other medium for publication or distribution or for any 
                commercial enterprise, without IpromiseAfrica’s express prior written consent.
              </p>
              <p><strong>You agree not to:</strong></p>
              <ul>
                <li>Attempt to gain unauthorized access to any portion or feature of the Site</li>
                <li>Probe, scan or test the vulnerability of the Site or any network connected to the Site</li>
                <li>Reverse look-up, trace or seek to trace any information on any other user</li>
                <li>Take any action that imposes an unreasonable load on the infrastructure of the Site</li>
                <li>Use any device to interfere with the proper working of the Site</li>
                <li>Forge headers or otherwise manipulate identifiers</li>
                <li>Impersonate any other individual or entity</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Accounts -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('accounts')">
              <span>Accounts, Passwords and Security</span>
              <i :class="dropdownStates.accounts ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.accounts">
              <p>
                Certain features or services offered on or through the Site may require you to open an 
                account. You are entirely 
                responsible for maintaining the confidentiality of the information you hold for your 
                account, including your password, and for any and all activity that occurs under your 
                account as a result of your failing to keep this information secure and confidential.
              </p>
              <p>
                You agree to notify IpromiseAfrica immediately of any unauthorized use of your account 
                or password, or any other breach of security. You may be held liable for losses incurred 
                by IpromiseAfrica or any other user of or visitor to the Site due to someone else using 
                your IpromiseAfrica ID, password or account as a result of your failing to keep your 
                account information secure and confidential.
              </p>
              <p>
                You may not use anyone else’s IpromiseAfrica ID, password or account at any time without 
                the express permission and consent of the holder of that IpromiseAfrica ID, password or 
                account. IpromiseAfrica cannot and will not be liable for any loss or damage arising from 
                your failure to comply with these obligations.
              </p>
            </div>
          </div>
        </div>

        <!-- Privacy -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('privacy')">
              <span>Privacy</span>
              <i :class="dropdownStates.privacy ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.privacy">
              <p>
                IpromiseAfrica's <router-link to="/ipromiseafrica/legal/internet-services/privacy-policy">Privacy Policy</router-link> 
                applies to use of this Site, and its terms are made a part of these Terms of Use by this 
                reference. Additionally, by using the Site, you acknowledge and agree that Internet 
                transmissions are never completely private or secure. You understand that any message or 
                information you send to the Site may be read or intercepted by others, even if there is a 
                special notice that a particular transmission (for example, credit card information) is 
                encrypted.
              </p>
            </div>
          </div>
        </div>

        <!-- Disclaimers -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('disclaimers')">
              <span>Disclaimers</span>
              <i :class="dropdownStates.disclaimers ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.disclaimers">
              <p>
                IPROMISEAFRICA DOES NOT PROMISE THAT THE SITE OR ANY CONTENT, SERVICE OR FEATURE OF THE 
                SITE WILL BE ERROR-FREE OR UNINTERRUPTED, OR THAT ANY DEFECTS WILL BE CORRECTED, OR THAT 
                YOUR USE OF THE SITE WILL PROVIDE SPECIFIC RESULTS. THE SITE AND ITS CONTENT ARE DELIVERED 
                ON AN "AS-IS" AND "AS-AVAILABLE" BASIS.
              </p>
              <p>
                ALL INFORMATION PROVIDED ON THE SITE IS SUBJECT TO CHANGE WITHOUT NOTICE. IPROMISEAFRICA 
                CANNOT ENSURE THAT ANY FILES OR OTHER DATA YOU DOWNLOAD FROM THE SITE WILL BE FREE OF 
                VIRUSES OR CONTAMINATION OR DESTRUCTIVE FEATURES. IPROMISEAFRICA DISCLAIMS ALL WARRANTIES, 
                EXPRESS OR IMPLIED, INCLUDING ANY WARRANTIES OF ACCURACY, NON-INFRINGEMENT, MERCHANTABILITY 
                AND FITNESS FOR A PARTICULAR PURPOSE.
              </p>
              <p>
                IPROMISEAFRICA DISCLAIMS ANY AND ALL LIABILITY FOR THE ACTS, OMISSIONS AND CONDUCT OF ANY 
                THIRD PARTIES IN CONNECTION WITH OR RELATED TO YOUR USE OF THE SITE AND/OR ANY IPROMISEAFRICA 
                SERVICES. YOU ASSUME TOTAL RESPONSIBILITY FOR YOUR USE OF THE SITE AND ANY LINKED SITES. 
                YOUR SOLE REMEDY AGAINST IPROMISEAFRICA FOR DISSATISFACTION WITH THE SITE OR ANY CONTENT 
                IS TO STOP USING THE SITE OR ANY SUCH CONTENT.
              </p>
              <p>
                IpromiseAfrica reserves the right to do any of the following, at any time, without notice:
              </p>
              <ul>
                <li>To modify, suspend or terminate operation of or access to the Site</li>
                <li>To modify or change the Site, and any applicable policies or terms</li>
                <li>To interrupt the operation of the Site as necessary for maintenance</li>
              </ul>
            </div>
          </div>
        </div>


        <!-- Indemnity -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('indemnity')">
              <span>Indemnity</span>
              <i :class="dropdownStates.indemnity ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.indemnity">
              <p>
                You agree to indemnify and hold IpromiseAfrica, its officers, directors, shareholders, 
                employees, agents, subsidiaries and affiliates, harmless from any demands, loss, liability, 
                claims or expenses (including attorneys' fees), made against IpromiseAfrica by any third 
                party due to or arising out of or in connection with your use of the Site.
              </p>
            </div>
          </div>
        </div>

        <!-- Violation of Terms -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('violation')">
              <span>Violation of These Terms of Use</span>
              <i :class="dropdownStates.violation ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.violation">
              <p>
                IpromiseAfrica may disclose any information we have about you (including your identity) if 
                we determine that such disclosure is necessary in connection with any investigation or 
                complaint regarding your use of the Site, or to identify, contact or bring legal action 
                against someone who may be causing injury to or interference with (either intentionally or 
                unintentionally) IpromiseAfrica's rights or property, or the rights or property of visitors 
                to or users of the Site, including IpromiseAfrica's customers.
              </p>
              <p>
                IpromiseAfrica reserves the right at all times to disclose any information that IpromiseAfrica 
                deems necessary to comply with any applicable law, regulation, legal process or governmental 
                request. IpromiseAfrica also may disclose your information when IpromiseAfrica determines 
                that applicable law requires or permits such disclosure, including exchanging information 
                with other companies and organizations for fraud protection purposes.
              </p>
              <p><strong>Termination of Access:</strong></p>
              <p>
                You agree that IpromiseAfrica may, in its sole discretion and without prior notice, 
                terminate your access to the Site and/or block your future access to the Site if we determine 
                that you have violated these Terms of Use. You also agree that any violation by you of these 
                Terms of Use will constitute an unlawful and unfair business practice, and will cause 
                irreparable harm to IpromiseAfrica, for which monetary damages would be inadequate.
              </p>
              <p>
                If IpromiseAfrica does take any legal action against you as a result of your violation of 
                these Terms of Use, IpromiseAfrica will be entitled to recover from you, and you agree to 
                pay, all reasonable attorneys' fees and costs of such action, in addition to any other 
                relief granted to IpromiseAfrica.
              </p>
            </div>
          </div>
        </div>

        <!-- Feedback and Information -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('feedback')">
              <span>Feedback and Information</span>
              <i :class="dropdownStates.feedback ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.feedback">
              <p>
                Any feedback you provide at this site shall be deemed to be non-confidential. 
                IpromiseAfrica shall be free to use such information on an unrestricted basis.
              </p>
              <p>
                The information contained in this web site is subject to change without notice.
              </p>
            </div>
          </div>
        </div>

        <!-- Footer Notice -->
        <div class="legal-footer-notice">
          <p>Copyright © 2026 IpromiseAfrica Inc. All rights reserved.</p>
          <p>IpromiseAfrica Inc., Nairobi, Republic of Kenya.</p>
          <p>Updated by The IpromiseAfrica Legal Team on April. 27, 2026</p>
        </div>

        <!-- Action Buttons -->
        <div class="legal-actions">
          <button class="accept-btn" @click="acceptTerms">
            <i class="fa-solid fa-check"></i>
            Proceed to Privacy Policy
          </button>
          <router-link to="/" class="back-btn">
            <i class="fa-solid fa-arrow-left"></i>
            Back to Home
          </router-link>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import LegalDocumentSeal from "../../components/legal/LegalDocumentSeal.vue";

export default {
  name: "TermsOfUse",
  components: {
    MainNavbar,
    MainFooter,
    LegalDocumentSeal,
  },
  data() {
    return {
      dropdownStates: {
        ownership: true,
        content: true,
        accounts: true,
        privacy: true,
        disclaimers: false,
        liability: false,
        indemnity: false,
        violation: false,
        feedback: false
      }
    };
  },
  methods: {
    toggleDropdown(section) {
      this.dropdownStates[section] = !this.dropdownStates[section];
    },
    acceptTerms() {
      // Store acceptance in localStorage
      localStorage.setItem('termsAccepted', 'true');
      this.$router.push('/ipromiseafrica/legal/internet-services/privacy-policy');
    }
  },
  mounted() {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  font-family: "Poppins", sans-serif;
  background: var(--background-color1);
  color: var(--text-color1);
  overflow-x: clip;
}

/* Hero */
.legal-hero {
  background: linear-gradient(
    145deg,
    var(--background-color2) 0%,
    color-mix(in srgb, var(--background-color1) 88%, var(--background-color2)) 100%
  );
  color: var(--text-color1);
  padding: clamp(3rem, 8vw, 5rem) clamp(1rem, 4vw, 1.5rem);
  text-align: center;
  margin-top: max(70px, calc(env(safe-area-inset-top, 0px) + 64px));
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.legal-hero .hero-content {
  position: relative;
  z-index: 2;
}

.hero-content h1 {
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 800;
  margin-bottom: 10px;
  color: var(--text-color1);
  letter-spacing: 0.02em;
}

.hero-content p {
  color: var(--text-color2);
  font-size: clamp(0.95rem, 2vw, 1.1rem);
}

/* Main content */
.legal-container {
  flex: 1 0 auto;
  width: 100%;
  max-width: min(1200px, 100% - 2rem);
  margin: clamp(1.5rem, 4vw, 2.5rem) auto;
  padding: 0 clamp(0.85rem, 3vw, 1.25rem);
  box-sizing: border-box;
}

.legal-content {
  background: var(--background-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 20%, transparent);
  border-radius: clamp(10px, 2vw, 14px);
  box-shadow: 0 8px 28px color-mix(in srgb, var(--border-color-hover) 6%, transparent);
  padding: clamp(1.25rem, 4vw, 2.5rem);
}

.legal-section {
  margin-bottom: clamp(1.25rem, 3vw, 1.75rem);
}

.legal-section h2 {
  color: var(--text-color1);
  font-size: clamp(1.35rem, 3vw, 1.8rem);
  font-weight: 700;
  margin-bottom: 1rem;
  padding-bottom: 0.65rem;
  border-bottom: 2px solid var(--border-color-hover);
}

.legal-intro {
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  line-height: 1.7;
  color: var(--text-color2);
  margin-bottom: 1.25rem;
}

.legal-notice {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border-left: 4px solid var(--border-color-hover);
  padding: clamp(0.85rem, 2vw, 1rem);
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  line-height: 1.6;
  color: var(--text-color2);
  border-radius: 0 8px 8px 0;
}

.legal-notice strong {
  color: var(--text-color1);
}

/* Dropdown sections */
.dropdown-section {
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 18%, transparent);
  border-radius: 10px;
  margin-bottom: 0.85rem;
  overflow: hidden;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.dropdown-section:hover {
  border-color: color-mix(in srgb, var(--border-color-hover) 45%, transparent);
}

.dropdown-header {
  width: 100%;
  padding: clamp(0.85rem, 2.5vw, 1.25rem);
  background: var(--background-color2);
  border: none;
  text-align: left;
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  font-weight: 600;
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  transition: background 0.25s ease, color 0.25s ease;
}

.dropdown-header:hover {
  background: color-mix(in srgb, var(--border-color-hover) 8%, var(--background-color2));
}

.dropdown-header i {
  color: var(--border-color-hover);
  font-size: 0.9rem;
  flex-shrink: 0;
  transition: transform 0.25s ease;
}

.dropdown-content {
  padding: clamp(1rem, 3vw, 1.5rem);
  background: color-mix(in srgb, var(--background-color2) 55%, var(--background-color1));
  border-top: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  color: var(--text-color2);
  animation: slideDown 0.3s ease;
}

.dropdown-content p {
  color: var(--text-color2);
  line-height: 1.65;
  margin-bottom: 0.85rem;
}

.dropdown-content ul {
  padding-left: 1.25rem;
  margin: 0.85rem 0;
}

.dropdown-content li {
  margin-bottom: 0.65rem;
  line-height: 1.6;
  color: var(--text-color2);
}

.dropdown-content strong {
  color: var(--text-color1);
}

.dropdown-content :deep(a) {
  color: var(--border-color-hover);
  font-weight: 600;
  text-decoration: none;
}

.dropdown-content :deep(a:hover) {
  text-decoration: underline;
}

/* Footer notice */
.legal-footer-notice {
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  padding-top: 1.25rem;
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
  text-align: center;
  color: var(--text-color2);
  font-size: clamp(0.82rem, 1.8vw, 0.9rem);
}

.legal-footer-notice p {
  margin: 0.35rem 0;
}

/* Action buttons */
.legal-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(0.75rem, 2vw, 1.25rem);
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  padding-top: clamp(1.25rem, 3vw, 1.75rem);
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
}

.accept-btn {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: 1px solid var(--border-color-hover);
  padding: clamp(0.65rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.75rem);
  border-radius: 32px;
  font-weight: 600;
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.accept-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--border-color-hover) 28%, transparent);
}

.back-btn {
  background: var(--background-color2);
  color: var(--text-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 25%, transparent);
  padding: clamp(0.65rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.75rem);
  border-radius: 32px;
  font-weight: 600;
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.back-btn:hover {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .legal-hero {
    padding: clamp(2.5rem, 7vw, 3.75rem) clamp(0.85rem, 3vw, 1rem);
  }

  .legal-content {
    padding: clamp(1rem, 3.5vw, 1.5rem);
  }

  .dropdown-header {
    font-size: 0.95rem;
  }

  .legal-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .accept-btn,
  .back-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .dropdown-content {
    padding: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-content {
    animation: none;
  }

  .accept-btn:hover,
  .back-btn:hover {
    transform: none;
  }
}
</style>